import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================================================
// Precast Configuration Store
// Rule center for the BIM Precast tab dialogs and automation tools:
// split (panel) rules, mounting parts defaults, reinforcement standards and
// fabrication/shop-drawing settings. Persisted across sessions like the
// Manage settings store. All lengths are millimeters, weights kilograms.
// ============================================================================

/** Joint (derz) gap presets used between adjacent panels. */
export type PrecastJointGap = 10 | 15 | 20 | 25 | 30;

/** Reinforcement style applied by the automatic precast rebar tool. */
export type PrecastReinforcementStyle = 'Welded Wire Mesh' | 'Single Layer Bars' | 'Double Layer Bars';

/** CAM machine format families exported by the Fabrication panel. */
export type PrecastCamFormat = 'PXML' | 'Unitechnik (XML)' | 'Bar Bending CSV' | 'Rebar Cut List (CSV)';

/** Ordered CAM format list used by the Configuration and CAM Export dialogs. */
export const PRECAST_CAM_FORMATS: PrecastCamFormat[] = [
    'PXML',
    'Unitechnik (XML)',
    'Bar Bending CSV',
    'Rebar Cut List (CSV)',
];

/** Shop drawing sheet size presets (landscape, millimeters). */
export interface PrecastSheetSizePreset {
    id: string;
    label: string;
    widthMm: number;
    heightMm: number;
}

export const PRECAST_SHEET_SIZES: PrecastSheetSizePreset[] = [
    { id: 'A4L', label: 'A4 Landscape (297×210)', widthMm: 297, heightMm: 210 },
    { id: 'A3L', label: 'A3 Landscape (420×297)', widthMm: 420, heightMm: 297 },
    { id: 'A2L', label: 'A2 Landscape (594×420)', widthMm: 594, heightMm: 420 },
    { id: 'A1L', label: 'A1 Landscape (841×594)', widthMm: 841, heightMm: 594 },
];

export interface PrecastConfiguration {
    // ---- Split rules (transport & production constraints) ----
    /** Maximum panel width a factory bed / mold can cast, mm. */
    maxPanelWidthMm: number;
    /** Maximum panel height, mm (wall) — truck/tower-crane envelope. */
    maxPanelHeightMm: number;
    /** Maximum finished weight per panel, kg. */
    maxPanelWeightKg: number;
    /** Vertical joint gap reserved between wall panels, mm. */
    jointGapMm: PrecastJointGap;
    /** End gap trimmed at each monolithic wall boundary before splitting, mm. */
    edgeGapMm: number;
    /** Floor slab mode: hollow core planks vs solid precast slabs. */
    floorMode: 'HollowCore' | 'SolidSlab';
    /** Hollow-core plank nominal width, mm (standard bed widths). */
    hollowCoreWidthMm: number;
    /** Minimum allowed panel width, mm (panels below this get merged). */
    minPanelWidthMm: number;

    // ---- Reinforcement standards ----
    reinforcementStyle: PrecastReinforcementStyle;
    /** Mesh bar diameter, mm. */
    rebarDiameterMm: number;
    /** Mesh bar spacing (both directions), mm. */
    rebarSpacingMm: number;
    /** Clear cover to mesh, mm. */
    coverMm: number;
    /** Concrete unit weight for panel weight checks, kg/m³. */
    concreteDensityKgM3: number;

    // ---- Mounting parts defaults ----
    /** Lifting anchors auto-placed per wall panel (1 or 2). */
    liftingAnchorsPerPanel: 1 | 2;
    /** Default embedded plate size, mm. */
    defaultPlateSizeMm: number;

    // ---- Fabrication / shop drawings ----
    /** Drawing scale denominator (e.g. 20 = 1:20). */
    shopDrawingScale: number;
    shopSheetSizeId: string;
    /** Include the reinforcement layer on shop sheets. */
    showReinforcement: boolean;
    /** Include the embedded-parts layer on shop sheets. */
    showMountingParts: boolean;
    /** Include the bar/quantity takeoff table on shop sheets. */
    showSchedule: boolean;
    /** Default CAM export format for the Fabrication panel. */
    camFormat: PrecastCamFormat;
    /** Factory machine preset written into CAM headers. */
    factoryName: string;
}

const defaultConfig: PrecastConfiguration = {
    maxPanelWidthMm: 3000,
    maxPanelHeightMm: 3300,
    maxPanelWeightKg: 7500,
    jointGapMm: 20,
    edgeGapMm: 0,
    floorMode: 'HollowCore',
    hollowCoreWidthMm: 1200,
    minPanelWidthMm: 600,

    reinforcementStyle: 'Welded Wire Mesh',
    rebarDiameterMm: 6,
    rebarSpacingMm: 200,
    coverMm: 25,
    concreteDensityKgM3: 2400,

    liftingAnchorsPerPanel: 2,
    defaultPlateSizeMm: 150,

    shopDrawingScale: 20,
    shopSheetSizeId: 'A2L',
    showReinforcement: true,
    showMountingParts: true,
    showSchedule: true,
    camFormat: 'PXML',
    factoryName: 'Yap Precast Plant',
};

interface PrecastConfigState {
    config: PrecastConfiguration;
    /** Patch one or more configuration fields (dialog live-editing). */
    updateConfig: (patch: Partial<PrecastConfiguration>) => void;
    /** Restore factory defaults (Configuration dialog Reset). */
    resetConfig: () => void;
}

export const usePrecastConfigStore = create<PrecastConfigState>()(
    persist(
        (set) => ({
            config: { ...defaultConfig },
            updateConfig: (patch) => set((state) => ({ config: { ...state.config, ...patch } })),
            resetConfig: () => set({ config: { ...defaultConfig } }),
        }),
        { name: 'yap-precast-config' }
    )
);

/** Non-reactive read used by tools/geometry services. */
export function getPrecastConfig(): PrecastConfiguration {
    return usePrecastConfigStore.getState().config;
}
